import React, { useRef } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { IconPhoto } from '@tabler/icons-react';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import { CollapsibleSection } from '@/admin/components/skill/collapsible-section';
import type { User } from '@/admin/lib/api';
import type { BlogFormData } from '@/admin/pages/blog-detail';

interface BlogBasicInfoSectionProps {
  control: Control<BlogFormData>;
  users: User[];
  loadingUsers: boolean;
  coverImageUrl?: string;
  onCoverUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  uploadingCover: boolean;
}

export const BlogBasicInfoSection: React.FC<BlogBasicInfoSectionProps> = ({
  control,
  users,
  loadingUsers,
  coverImageUrl,
  onCoverUpload,
  uploadingCover,
}) => {
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleCoverChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await onCoverUpload(e);
    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  };

  return (
    <CollapsibleSection
      id="basic-info"
      title="Basic Info & Cover"
      description="Fundamental details and cover image"
      defaultOpen={true}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: Form Fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input {...field} id="title" placeholder="Blog title" />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Input
                    {...field}
                    id="slug"
                    placeholder="blog-slug"
                    className="font-mono text-sm"
                  />
                </div>
              )}
            />
            <p className="text-[10px] text-muted-foreground">
              URL-friendly identifier. Changing this will change the blog URL.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="author">Author</Label>
            <Controller
              name="author_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={loadingUsers}
                >
                  <SelectTrigger id="author">
                    <SelectValue placeholder="Select author" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.display_name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Right Column: Cover Image */}
        <div className="space-y-2">
          <Label>Cover Image</Label>
          <div
            className={`relative group border rounded-lg overflow-hidden cursor-pointer transition-all aspect-video ${
              coverImageUrl
                ? 'hover:ring-2 ring-primary/30'
                : 'border-dashed border-2 hover:border-primary/50'
            } ${uploadingCover ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={() => coverInputRef.current?.click()}
          >
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverChange}
              className="hidden"
            />
            {coverImageUrl ? (
              <div className="relative w-full h-full">
                <img
                  src={coverImageUrl}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="text-white text-[11px] font-medium flex items-center gap-1.5">
                    <IconPhoto className="h-3.5 w-3.5" />
                    Change Cover
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground bg-muted/30">
                <IconPhoto className="h-8 w-8 mb-2" />
                <span className="text-xs font-medium">
                  {uploadingCover ? 'Uploading...' : 'Upload Cover Image'}
                </span>
                <span className="text-[10px] text-muted-foreground mt-1">
                  1200x630 recommended
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
